import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Contact", path: "/contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-[#040A12]/90 backdrop-blur-xl border-b border-[#C6A75E]/15"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 flex items-center justify-between h-20">

        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/4oti298i_KSG%20GROUP.jpg"
            alt="KSG Realty"
            className="w-10 h-10 object-cover rounded-sm opacity-90 group-hover:opacity-100 transition-opacity duration-500"
          />
          <div className="hidden sm:block">
            <span className="font-serif text-white text-sm font-light tracking-[0.15em] uppercase">KSG</span>
            <span className="font-sans text-[#C6A75E]/70 text-[10px] uppercase tracking-[0.25em] block" style={{ marginTop: '-2px' }}>Realty</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}-link`}
              className={`font-sans text-[10px] uppercase tracking-[0.2em] transition-colors duration-400 ${
                pathname === link.path
                  ? "text-[#C6A75E]"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <Link
          to="/contact"
          data-testid="nav-cta-button"
          className="hidden md:block px-6 py-2.5 border border-[#C6A75E]/50 text-[#C6A75E] font-sans text-[10px] uppercase tracking-[0.2em] hover:bg-[#C6A75E]/8 hover:border-[#C6A75E] transition-all duration-500"
        >
          Initiate a Conversation
        </Link>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-button"
          className="md:hidden text-slate-400 hover:text-white transition-colors p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={18} strokeWidth={1} /> : <Menu size={18} strokeWidth={1} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#040A12]/98 backdrop-blur-xl border-t border-[#C6A75E]/10 px-6 py-8 flex flex-col gap-6"
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
              className={`font-sans text-xs uppercase tracking-[0.2em] transition-colors duration-400 ${
                pathname === link.path ? "text-[#C6A75E]" : "text-slate-400"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="mt-2 px-6 py-3 border border-[#C6A75E]/50 text-[#C6A75E] font-sans text-[10px] uppercase tracking-[0.2em] text-center"
          >
            Initiate a Conversation
          </Link>
        </div>
      )}
    </header>
  );
}
